import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Search, ExternalLink } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

export default function SpotifyPicker({ initialQuery = "", onClose, onPick }) {
  const [q, setQ] = useState(initialQuery);
  const [items, setItems] = useState([]);
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [pasteUrl, setPasteUrl] = useState("");
  const [pasteBusy, setPasteBusy] = useState(false);

  useEffect(() => {
    if (!q || q.trim().length < 2) { setItems([]); setHint(""); return; }
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`/spotify/search?q=${encodeURIComponent(q.trim())}`);
        setItems(data.items || []);
        setHint(data.hint || "");
      } catch (e) {
        setItems([]);
        setHint("Search unavailable — paste your artist URL below.");
      } finally {
        setBusy(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const attachPastedUrl = async () => {
    const url = pasteUrl.trim();
    if (!url) return;
    setPasteBusy(true);
    try {
      const { data } = await api.post(`/spotify/from_url`, { url });
      onPick(data);
    } catch (err) {
      toast.error(formatApiError(err, "That URL didn't work"));
    } finally { setPasteBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0b1220] border-white/10 text-white sm:max-w-lg" data-testid="spotify-picker">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Link Spotify artist</DialogTitle>
          <DialogDescription className="text-slate-400">
            Search Spotify for an existing artist, or paste your open.spotify.com/artist URL.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-white/10 bg-white/5 flex items-center gap-2 px-3 py-2.5">
          <Search size={14} className="text-slate-400" />
          <input
            autoFocus
            data-testid="spotify-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by artist name…"
            className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-slate-500"
          />
          {busy && <Loader2 size={14} className="animate-spin text-slate-400" />}
        </div>
        <div className="max-h-[280px] overflow-y-auto space-y-2">
          {items.map((a) => (
            <button
              type="button"
              key={a.id}
              data-testid={`spotify-result-${a.id}`}
              onClick={() => onPick(a)}
              className="w-full text-left rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] p-2.5 flex items-center gap-3"
            >
              {a.image ? (
                <img src={a.image} alt="" className="h-10 w-10 rounded-full object-cover" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-white/5 flex items-center justify-center text-xs text-slate-400">?</div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white truncate">{a.name}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {(a.followers || 0).toLocaleString()} followers{a.genres?.length ? ` · ${a.genres.slice(0, 2).join(", ")}` : ""}
                </div>
              </div>
            </button>
          ))}
          {!busy && items.length === 0 && q.trim().length >= 2 && (
            <div className="text-xs text-slate-500 px-1">
              {hint || "No matches. Paste your Spotify artist URL below instead."}
            </div>
          )}
        </div>
        <div className="pt-3 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Or paste a URL</div>
          <div className="flex items-center gap-2">
            <input
              data-testid="spotify-paste-url"
              value={pasteUrl}
              onChange={(e) => setPasteUrl(e.target.value)}
              placeholder="https://open.spotify.com/artist/…"
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />
            <button
              type="button"
              data-testid="spotify-attach"
              onClick={attachPastedUrl}
              disabled={pasteBusy || !pasteUrl.trim()}
              className="inline-flex items-center gap-1.5 rounded-full bg-white text-black text-sm px-4 py-2 hover:bg-slate-200 disabled:opacity-60"
            >
              {pasteBusy ? <Loader2 size={13} className="animate-spin" /> : "Link"}
            </button>
          </div>
          <div className="mt-3 flex items-center justify-between text-xs">
            <span className="text-slate-500">Don&apos;t have a Spotify profile?</span>
            <a
              href="https://artists.spotify.com/signup"
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300"
            >
              Create one on Spotify for Artists <ExternalLink size={11} />
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
